/**
 * @author Shalini
 * @name ErrorListComponent
 * @desc File Upload error list
 */
import React from 'react';
import PropTypes from 'prop-types';
import { MaterialUiTable as _MaterialTable } from '@gscope-mfe/common-libs';
import { MaterialUiCore } from '@gscope-mfe/common-libs';
import TableIcons from '../TableIcons';
import { CsvBuilder } from 'filefy';
import { Services, AppUtils } from '@gscope-mfe/app-bridge';
import { loadLangCommon } from '../LocalizeLang';
import enData from './lang/en.json';
import esData from './lang/es.json';
import cmnData from './lang/cmn.json';
import frData from './lang/fr.json';

const { TablePagination } = MaterialUiCore;
const { uploadErrorList, downloadErrorReport } = Services;
const { default: MaterialTable } = _MaterialTable;

const ErrorListComponent = ({ transactionId, status, bulkUpdateHistory, trans }) => {
    const [errorList, setErrorList] = React.useState([]);

    const { setloading } = AppUtils.get();
    const [columns, setColumns] = React.useState([
        { field: 'lineNumber', title: trans('bulkUpload.rowNum') },
        { field: 'description', title: trans('bulkUpload.description') },
    ]);

    const ERRORED_DATA_FILE_NAME = 'Errored_data.csv';
    const ERROR_TABLE_FILE_NAME = 'Error_table.csv';

    const downloadErrorWithOriginalRow = async (transactionId) => {
        const resFile = await downloadErrorReport(transactionId);
        const url = window.URL.createObjectURL(new Blob([resFile.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', ERRORED_DATA_FILE_NAME);
        link.download = ERRORED_DATA_FILE_NAME;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    };

    const downloadErrors = () => {
        const data = errorList.map((rowData) =>
            columns.map(function (columnDef) {
                return rowData[columnDef.field];
            }),
        );

        new CsvBuilder(ERROR_TABLE_FILE_NAME)
            .setDelimeter(',')
            .setColumns(columns.map((columnDef) => columnDef.title))
            .addRows(data)
            .exportFile();
    };

    const downloadErrorFiles = async (status, transactionId) => {
        if (status == 'S') {
            await downloadErrorWithOriginalRow(transactionId);
        }
        downloadErrors();
    };

    /**
     * @name fetchErrorList
     * @desc gets the list of errors for a file upload
     */
    const fetchErrorList = async () => {
        setloading(true);
        const { data } = await uploadErrorList(transactionId);
        if (data.payload && data.payload.transactionDetails && data.payload.transactionDetails.errors) {
            const errors = data.payload.transactionDetails.errors;

            if (bulkUpdateHistory && errors.length) {
                // The line below is to change the column title in the table for the row identifier if it is a bulk update
                if (errors[0].rowIdentifier) {
                    setColumns((value) => {
                        value[0].title = errors[0].rowIdentifier.split('=')[0];
                        return value;
                    });
                }

                const errorArray = data.payload.transactionDetails.errors.map((err) => {
                    return {
                        description: err.description,
                        lineNumber: err.rowIdentifier ? err.rowIdentifier.split('=')[1] : err.lineNumber,
                    };
                });

                setErrorList(errorArray);
            } else setErrorList(errors);
        } else {
            setErrorList([]);
        }
        setloading(false);
    };

    /**
     * @name getTableRows
     * @desc get the rows for table errors
     */

    const getTableRows = () => errorList.map((error) => error);

    React.useEffect(() => {
        fetchErrorList();
    }, []);
    return (
        <>
            <MaterialTable
                columns={columns}
                data={getTableRows()}
                title=""
                icons={TableIcons}
                options={{
                    exportButton: true,
                    exportCsv: (d, c) => downloadErrorFiles(status, transactionId),
                    search: true,
                    pageSize: 10,
                    pageSizeOptions: [10, 20, 30, 40, 50, 100],
                    showEmptyDataSourceMessage: false,
                    emptyRowsWhenPaging: false,
                    headerStyle: {
                        backgroundColor: '#f4f4f4',
                        color: 'black',
                    },
                }}
                localization={{
                    toolbar: {
                        searchPlaceholder: `${trans('bulkupload.search')}`,
                    },
                }}
                components={{
                    Pagination: (props) => {
                        const newProps = {
                            ...props,
                            labelRowsPerPage: trans('bulkUpload.itemsPerPage'),
                            SelectProps: {
                                inputProps: { 'aria-label': trans('bulkUpload.itemsPerPage') },
                            },
                        };
                        return (
                            <TablePagination
                                style={newProps.style}
                                rowsPerPageOptions={newProps.rowsPerPageOptions}
                                rowsPerPage={+newProps.rowsPerPage}
                                colSpan={newProps.colSpan}
                                count={newProps.count}
                                page={+newProps.page}
                                SelectProps={newProps.SelectProps}
                                onChangePage={newProps.onChangePage}
                                icons={newProps.icons}
                                onChangeRowsPerPage={newProps.onChangeRowsPerPage}
                                labelRowsPerPage={newProps.labelRowsPerPage}
                                labelDisplayedRows={({ from, to, count }) => {
                                    return '' + from + '-' + to + ` ${trans('bulkUpload.of')} ` + count;
                                }}
                            />
                        );
                    },
                }}
            />
        </>
    );
};

ErrorListComponent.defaultProps = {
    transactionId: null,
};
ErrorListComponent.propTypes = {
    transactionId: PropTypes.string,
};
export default ErrorListComponent;
